/**
 * SplitNoticeBanner — tiered teacher notice for split-grade units.
 *
 *   runs_divided (Tier 1)  amber banner  — one grade works alone a while
 *   plan_two     (Tier 2)  steel-blue note — same lesson, two tasks/depths
 *   positive               mint note      — Unit 4, no separate planning
 *
 * Amber is the loudest by design; steel blue reads as information, not
 * warning. All three share padding, radius and type scale so they read as
 * siblings at different weights. Brand tokens only — no new colours.
 */
import React from "react";
import { AlertTriangle, Info, CheckCircle2 } from "lucide-react";

const TIERS = {
  runs_divided: {
    cls: "border-amber-300 bg-amber-50 text-amber-900",
    titleCls: "text-amber-950",
    Icon: AlertTriangle,
    label: "Some lessons run separately",
  },
  plan_two: {
    cls: "border-[#4a6984]/25 bg-[#4a6984]/10 text-[#1c2d3d]",
    titleCls: "text-[#1c2d3d]",
    Icon: Info,
    label: "Some lessons need two tasks",
  },
  positive: {
    cls: "border-[#96ded1]/60 bg-[#96ded1]/20 text-[#12463b]",
    titleCls: "text-[#12463b]",
    Icon: CheckCircle2,
    label: "No separate planning",
  },
};

const renderInline = (text) =>
  text.split("**").map((seg, i) =>
    i % 2 === 1 ? (
      <strong key={i} className="font-bold">
        {seg}
      </strong>
    ) : (
      <React.Fragment key={i}>{seg}</React.Fragment>
    )
  );

const SplitNoticeBanner = ({ notice }) => {
  if (!notice) return null;
  const t = TIERS[notice.tier] || TIERS.plan_two;
  const { Icon } = t;
  return (
    <div
      data-testid={`math-engine-notice-${notice.tier}`}
      className={`mb-3 flex items-start gap-2 rounded-xl border-2 px-4 py-3 text-sm max-w-3xl leading-relaxed ${t.cls}`}
    >
      <Icon className="h-4 w-4 mt-0.5 shrink-0" />
      <div>
        <p className={`font-bold ${t.titleCls}`}>{renderInline(notice.title)}</p>
        {(notice.body || []).map((para, i) => (
          <p key={i} className="mt-1.5 opacity-95">
            {renderInline(para)}
          </p>
        ))}
      </div>
    </div>
  );
};

export default SplitNoticeBanner;
